/**
 * AR Bird Race — flap your arms to fly. First bird to the finish line wins.
 *
 * Design mirrors `balloon-pop`:
 *
 *   - **Independent simMode.** Every peer runs its own scene fed by the
 *     shared seed from the WELCOME packet, so timing / cloud positions are
 *     identical. Nothing about the local player's pose is broadcast — only
 *     "I flapped" events, aggregated into a Leaderboard by the host.
 *
 *   - **AR passthrough fullscreen.** `SceneCamera.attach()` paints the
 *     front-camera feed onto the Phaser scene at depth -100; the Phaser
 *     canvas itself is transparent. The entire device viewport is the
 *     pose-detection surface.
 *
 *   - **Pose tracking** via the SDK's MediaPipe-backed `PoseTracker`
 *     (33 BlazePose landmarks). A flap is any vertical swing across the
 *     shoulder line (up→down or down→up), with a 200ms cooldown so a
 *     normal "swing your arms up and down like wings" motion racks up
 *     flaps continuously.
 *
 *   - **Race-to-the-line.** Each local flap pushes your bird forward
 *     along a horizontal track. Reach the finish line at the right edge
 *     (= FLAPS_TO_WIN flaps) and you win. There's no timer — speed is
 *     all that matters. Fast flaps (< FAST_FLAP_MS apart) spawn an
 *     extra puff trail behind the bird so sprinting feels snappy.
 */
import { SdkGameScene, Leaderboard, SceneCamera, PageBackground } from '@platform/sdk';

// ───────────────────────────────────────────────────────── tuning

/** Flaps needed to cross the finish line. Tune for race length. */
const FLAPS_TO_WIN = 20;

/** How fast the bird's rendered position lerps to its flap-count target. */
const BIRD_LERP_RATE = 6; // larger = snappier catch-up

/** Any two flaps closer than this are a "fast" flap and get extra puffs. */
const FAST_FLAP_MS = 450;

/** Maximum puffs on screen — cheap safety cap for low-end GPUs. */
const MAX_PUFFS = 40;

/**
 * Flap thresholds — kept deliberately forgiving. A "flap" is detected
 * purely from vertical wrist movement relative to the shoulder line:
 *
 *   - ARMS_UP_DY:   how far above the shoulder line both wrists must go
 *                   to count as "arms up". 0.02 ≈ a hand's width in
 *                   normalized image space, so a relaxed raise is enough.
 *   - ARMS_DOWN_DY: how far below the shoulder line both wrists must
 *                   drop to count as "arms down". A natural swing-down
 *                   lands around chest / waist height — well below the
 *                   shoulder, but usually nowhere near the hips.
 *
 * No lateral-spread requirement, no hip-level requirement: either arm
 * pattern (straight up-and-down, or out to the sides) works, so "just
 * swing your arms up and down" counts as a flap.
 */
const ARMS_UP_DY      = 0.02; // wrist.y < shoulder.y - this → arms are up
const ARMS_DOWN_DY    = 0.05; // wrist.y > shoulder.y + this → arms are down
const FLAP_COOLDOWN_MS = 200;
const FLAP_CYCLE_MAX_MS = 2000; // UP→DOWN→UP must complete within this window
const VISIBILITY_MIN = 0.45;    // skip frames where landmarks are too unreliable

const BIRD_TINTS = [
  0xffffff, // local player = white (no tint)
  0xff8080, // soft red
  0x80ff80, // soft green
  0x80c0ff, // soft blue
  0xffcc66, // warm yellow
  0xc080ff, // lavender
  0xff80c0, // pink
  0x80e0d0  // teal
];

// ───────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players, seed }) {
    // Transparent background so the camera feed shows through.
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    PageBackground.clear('#000');

    // Render the camera feed as a scene-level background at depth -100.
    SceneCamera.attach(this);

    // Background music for the whole match. Audio unlocks on first tap,
    // which already happened in the lobby.
    if (this.sdk.audio) this.sdk.audio.playMusic('music');

    // ── Match state ──────────────────────────────────────────────
    this.elapsed = 0; // only used for pulsing visuals, not for a timer
    this.matchOver = false;

    // Flap detector state machine — see _updateFlapDetector().
    this.flap = {
      state: 'idle',       // 'idle' | 'up' | 'down'
      cycleStartMs: 0,
      lastFlapMs: 0,
      armsUpLastFrame: false,
      armsDownLastFrame: false
    };
    this.localFlaps = 0;
    /** Timestamp of the last confirmed flap, for fast-flap detection. */
    this.lastFlapWallMs = 0;

    // Current rendered bird position (lerped toward its flap-count target).
    this.localBirdX = 0;
    this.localBirdY = 0;

    /**
     * Puff trail particles spawned on each flap. Normalized coordinates
     * (0..1 image space) for the x so the race track still makes sense
     * after a resize; y is raw pixels because the bird's flight lane is
     * pinned to a fraction of the scene height.
     * @type {Array<{x:number,y:number,vx:number,vy:number,r:number,t:number,duration:number,fast:boolean}>}
     */
    this.puffs = [];

    // Drifting clouds for a bit of motion even before the first pose lock.
    // Deterministic positions from the seeded RNG so every peer sees the
    // same background animation.
    this.clouds = [];
    for (let i = 0; i < 5; i++) {
      const big = this.sdk.rng.next() > 0.5;
      const tex = big ? 'cloud_large' : 'cloud_small';
      const y = 0.12 + this.sdk.rng.next() * 0.55;
      const x = this.sdk.rng.next();
      const speed = 0.01 + this.sdk.rng.next() * 0.03;
      const img = this.add.image(0, 0, tex);
      img.setDepth(-50);
      img.setAlpha(0.7);
      img.setScale(0.5 + this.sdk.rng.next() * 0.4);
      this.clouds.push({ x, y, speed, img });
    }

    // ── Leaderboard ──────────────────────────────────────────────
    this.lb = new Leaderboard({ sdk: this.sdk });
    this.lb.onUpdate(({ scores }) => this._renderHud(scores));

    // ── Player bird sprites ──────────────────────────────────────
    // The local player's bird flies left-to-right across the entire
    // screen. x is driven by flap count; y stays at a fixed flight lane
    // so the player can keep their arms in frame. Remote players show
    // up as icons on the "race track" HUD strip at the top.
    this.localBird = this._createBird({
      tint: BIRD_TINTS[0],
      scale: 0.25,
      depth: 50
    });
    this.localBirdFlapAnimT = 0;
    this._resetBirdLayout();

    // Puff trail layer, below the bird so the bird flies "out of" it.
    this.puffGraphics = this.add.graphics().setDepth(45);
    // Finish line layer, below the bird so the bird crosses over it.
    this.finishGraphics = this.add.graphics().setDepth(20);

    // ── Pose skeleton overlay ────────────────────────────────────
    this.skeletonGraphics = this.add.graphics().setDepth(40);
    // Separate graphics for the flap-arc guide so we can dim it once the
    // player starts nailing flaps.
    this.guideGraphics = this.add.graphics().setDepth(48);

    // ── HUD ──────────────────────────────────────────────────────
    this._buildHud();
    this._renderHud(this.lb.getAll());

    // Pre-existing Phaser scene shutdown cleans up bird sprites and
    // graphics automatically via Phaser's display list; no manual
    // teardown needed here.
  }

  // ─────────────────────────────────────────────────────────── tick

  tick(dt, _inputs) {
    if (this.matchOver) return;

    this.elapsed += dt;

    // Drift clouds laterally. Deterministic across peers because `dt` is
    // the fixed-timestep value and the seed decided their start positions.
    for (const c of this.clouds) {
      c.x -= c.speed * dt;
      if (c.x < -0.1) c.x = 1.1;
    }

    this._updateFlapDetector();
    this._updateBirdPosition(dt);
    this._updateLocalBirdAnim(dt);
    this._updatePuffs(dt);
    this._checkWinCondition();
    // NOTE: draw work happens in Phaser's update(), not here — a slow
    // device would make tick() fire multiple times per frame and we'd
    // waste cycles redrawing N times per visible frame.
  }

  /** Phaser per-frame render. Cheap: read-only of simulation state. */
  update(_time, _delta) {
    if (this.matchOver) return;
    this._renderFrame();
  }

  // ─────────────────────────────────────────────── flap detection

  _updateFlapDetector() {
    const pose = this.sdk.tracking && this.sdk.tracking.pose;
    if (!pose) return;

    const lS = pose.getLandmark('leftShoulder');
    const rS = pose.getLandmark('rightShoulder');
    const lW = pose.getLandmark('leftWrist');
    const rW = pose.getLandmark('rightWrist');
    if (!lS || !rS || !lW || !rW) {
      // Player stepped out of frame — don't reset the cycle, just wait.
      this._setStatus('Step back so the camera can see your shoulders & arms');
      return;
    }

    // Drop frames where the landmarks are too noisy. Both wrists and both
    // shoulders need to be at least reasonably visible or we can't trust
    // the reading and would emit spurious flaps on bad frames.
    const confident = (
      (lS.visibility == null || lS.visibility > VISIBILITY_MIN) &&
      (rS.visibility == null || rS.visibility > VISIBILITY_MIN) &&
      (lW.visibility == null || lW.visibility > VISIBILITY_MIN) &&
      (rW.visibility == null || rW.visibility > VISIBILITY_MIN)
    );
    if (!confident) return;

    const shoulderMidY = (lS.y + rS.y) * 0.5;

    // ARMS_UP = both wrists above the shoulder line (raised like wings).
    const armsUp =
      lW.y < shoulderMidY - ARMS_UP_DY &&
      rW.y < shoulderMidY - ARMS_UP_DY;

    // ARMS_DOWN = both wrists clearly below the shoulder line. We used to
    // require them below the hips and spread sideways, which killed most
    // natural flap motions — a normal swing-down lands around chest/waist
    // height, not at the hips. Dropping that requirement so any up→down
    // arm swing counts.
    const armsDown =
      lW.y > shoulderMidY + ARMS_DOWN_DY &&
      rW.y > shoulderMidY + ARMS_DOWN_DY;

    this.flap.armsUpLastFrame = armsUp;
    this.flap.armsDownLastFrame = armsDown;

    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());

    // Expire stale cycles so a player can't rest halfway and finish an
    // hour later. Keeps the motion crisp.
    if (this.flap.state !== 'idle' && now - this.flap.cycleStartMs > FLAP_CYCLE_MAX_MS) {
      this.flap.state = 'idle';
    }

    // State machine. A flap is any full direction change — up→down OR
    // down→up — which means even if you start with arms already raised,
    // the first swing down counts, and every swing after that counts too.
    //
    //   idle + armsUp   → up
    //   idle + armsDown → down
    //   up   + armsDown → 🎉 flap counted, now in 'down'
    //   down + armsUp   → 🎉 flap counted, now in 'up'
    //
    // A 200 ms cooldown keeps hovering at the threshold from spamming
    // flaps, and a 2s cycle expiry stops abandoned half-motions from
    // lingering.
    const tryFlap = () => {
      if (now - this.flap.lastFlapMs >= FLAP_COOLDOWN_MS) {
        this.flap.lastFlapMs = now;
        this._onFlap();
      }
    };

    switch (this.flap.state) {
      case 'idle':
        if (armsUp) {
          this.flap.state = 'up';
          this.flap.cycleStartMs = now;
          this._setStatus('Now sweep your arms DOWN');
        } else if (armsDown) {
          this.flap.state = 'down';
          this.flap.cycleStartMs = now;
          this._setStatus('Now lift your arms UP');
        } else {
          this._setStatus('Swing your arms up and down like wings');
        }
        break;

      case 'up':
        if (armsDown) {
          tryFlap();
          this.flap.state = 'down';
          this.flap.cycleStartMs = now;
          this._setStatus('And back UP — keep flapping!');
        }
        break;

      case 'down':
        if (armsUp) {
          tryFlap();
          this.flap.state = 'up';
          this.flap.cycleStartMs = now;
          this._setStatus('And back DOWN — keep flapping!');
        }
        break;
    }
  }

  _onFlap() {
    this.localFlaps += 1;
    this.lb.report(1);

    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const interval = this.lastFlapWallMs ? (now - this.lastFlapWallMs) : Infinity;
    this.lastFlapWallMs = now;
    const fast = interval < FAST_FLAP_MS;

    if (this.sdk.audio) {
      this.sdk.audio.playSfx('flap');
      // Fast flaps layer the heavier whoosh on top so sprinting sounds meaty.
      if (fast) this.sdk.audio.playSfx('whoosh', { volume: 0.6 });
    }

    this.localBirdFlapAnimT = 0.35;
    this._spawnPuffs(fast);
  }

  /**
   * Puffs spawn behind the bird (to its left) with a mild upward drift so
   * they look like disturbed air. Fast flaps spawn 5 puffs in a tight
   * burst; regular flaps spawn 2 lighter ones.
   */
  _spawnPuffs(fast) {
    const count = fast ? 5 : 2;
    for (let i = 0; i < count; i++) {
      // Hard-cap the puff count so we never drown a cheap GPU.
      if (this.puffs.length >= MAX_PUFFS) this.puffs.shift();
      const jitter = (Math.random() - 0.5);
      this.puffs.push({
        // Spawn slightly behind the bird (smaller x) so the trail reads
        // as "left behind" during forward motion.
        x: this.localBirdX - 20 - Math.random() * 16,
        y: this.localBirdY + jitter * 24,
        vx: -(50 + Math.random() * 60),
        vy: (Math.random() - 0.5) * 20 - 8,
        r: (fast ? 10 : 8) + Math.random() * 6,
        t: 0,
        duration: 0.7 + Math.random() * 0.3,
        fast
      });
    }
  }

  _updatePuffs(dt) {
    for (let i = this.puffs.length - 1; i >= 0; i--) {
      const p = this.puffs[i];
      p.t += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      // Gentle upward lift as they age (warm air effect).
      p.vy -= 8 * dt;
      if (p.t >= p.duration) this.puffs.splice(i, 1);
    }
  }

  // ─────────────────────────────────────────────────────── bird art

  /**
   * A bird is actually *two* sprites stacked — bird_up (wings raised)
   * and bird_down (wings lowered). We cross-fade between them to produce
   * the 2-frame flap animation; single-texture `setTexture` swaps would
   * look choppy at 60fps.
   */
  _createBird({ tint, scale, depth }) {
    const up = this.add.image(0, 0, 'bird_up')
      .setDepth(depth).setOrigin(0.5, 0.5).setScale(scale).setTint(tint);
    const down = this.add.image(0, 0, 'bird_down')
      .setDepth(depth).setOrigin(0.5, 0.5).setScale(scale).setTint(tint).setAlpha(0);
    return { up, down, scale, tint };
  }

  /**
   * Place the bird at the start of the race track. The race track is a
   * horizontal strip from START_X to FINISH_X at flight-lane-Y, pinned to
   * the current scene dimensions. Called at match start and on resize.
   */
  _resetBirdLayout() {
    const W = this.scale.width;
    const H = this.scale.height;
    this._trackStartX = W * 0.1;
    this._trackFinishX = W * 0.9;
    this._flightLaneY = H * 0.58;
    this.localBirdX = this._trackStartX;
    this.localBirdY = this._flightLaneY;
    this.localBird.up.setPosition(this.localBirdX, this.localBirdY);
    this.localBird.down.setPosition(this.localBirdX, this.localBirdY);
  }

  /**
   * Lerp the bird's rendered x toward its flap-count target. Each flap
   * bumps the target forward by (track_length / FLAPS_TO_WIN); the lerp
   * rate controls how snappy the catch-up feels. y stays on the flight
   * lane plus a small bob during the wing-flap animation.
   */
  _updateBirdPosition(dt) {
    const progress = Math.min(1, this.localFlaps / FLAPS_TO_WIN);
    const targetX = this._trackStartX + (this._trackFinishX - this._trackStartX) * progress;
    // Frame-rate-independent exponential lerp.
    const k = 1 - Math.exp(-BIRD_LERP_RATE * dt);
    this.localBirdX += (targetX - this.localBirdX) * k;
  }

  _updateLocalBirdAnim(dt) {
    // Count down the flap animation timer and cross-fade between the
    // wings-down and wings-up textures.
    const total = 0.35;
    const t = Math.max(0, this.localBirdFlapAnimT - dt);
    this.localBirdFlapAnimT = t;
    const phase = t > 0 ? t / total : 0;
    this.localBird.up.setAlpha(1 - phase);
    this.localBird.down.setAlpha(phase);
    // A subtle vertical bob riding on top of the flight-lane y.
    const bob = Math.sin((1 - phase) * Math.PI) * 8;
    this.localBirdY = this._flightLaneY - bob;
    this.localBird.up.setPosition(this.localBirdX, this.localBirdY);
    this.localBird.down.setPosition(this.localBirdX, this.localBirdY);
  }

  /**
   * The host is the only peer that can formally end the match. It watches
   * the authoritative leaderboard (aggregated from every peer's reports)
   * and calls endMatch() once anyone crosses the finish line.
   */
  _checkWinCondition() {
    if (this.matchOver) return;
    if (!this.sdk.isHost) return;
    const winner = this.lb.getWinner && this.lb.getWinner();
    if (winner && winner.score >= FLAPS_TO_WIN) {
      this._endMatch();
    }
  }

  // ─────────────────────────────────────────────── rendering helpers

  _renderFrame() {
    this._drawClouds();
    this._drawFinishLine();
    this._drawPuffs();
    this._drawSkeletonOverlay();
    this._drawFlapGuide();
  }

  /**
   * Draw a vertical finish-line banner at the right edge of the race
   * track. Checker stripes so it reads as "finish line" without needing
   * another asset download.
   */
  _drawFinishLine() {
    const g = this.finishGraphics;
    g.clear();
    const x = this._trackFinishX;
    const top = this._flightLaneY - 90;
    const bot = this._flightLaneY + 90;
    // Pole.
    g.lineStyle(4, 0xffffff, 0.85);
    g.beginPath();
    g.moveTo(x, top);
    g.lineTo(x, bot);
    g.strokePath();
    // Checker flag (a 3×5 grid).
    const fw = 48;
    const cellW = fw / 5;
    const cellH = 14;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 5; col++) {
        const black = (row + col) % 2 === 0;
        g.fillStyle(black ? 0x000000 : 0xffffff, 0.85);
        g.fillRect(x + col * cellW, top + row * cellH, cellW, cellH);
      }
    }
  }

  _drawPuffs() {
    const g = this.puffGraphics;
    g.clear();
    for (const p of this.puffs) {
      const life = p.t / p.duration;        // 0..1
      const grow = 1 + life * 1.4;           // puff balloons as it ages
      const alpha = (1 - life) * (p.fast ? 0.85 : 0.65);
      g.fillStyle(0xffffff, alpha);
      g.fillCircle(p.x, p.y, p.r * grow);
      // Inner rim for a bit of depth.
      if (p.fast) {
        g.fillStyle(0xaee6ff, alpha * 0.7);
        g.fillCircle(p.x - p.r * 0.2, p.y - p.r * 0.2, p.r * grow * 0.55);
      }
    }
  }

  _drawClouds() {
    const W = this.scale.width;
    const H = this.scale.height;
    for (const c of this.clouds) {
      c.img.setPosition(c.x * W, c.y * H);
    }
  }

  /**
   * Draw a thin connective skeleton from the live pose landmarks so the
   * player can see that the camera is tracking them. Mirrored on the
   * x-axis because the camera is front-facing (user-facing) and the
   * SDK mounts the video mirrored.
   */
  _drawSkeletonOverlay() {
    const g = this.skeletonGraphics;
    g.clear();

    const pose = this.sdk.tracking && this.sdk.tracking.pose;
    if (!pose) return;

    // The set of named pairs we actually care about — arms + torso + legs.
    // Face landmarks are skipped to keep the overlay uncluttered.
    const PAIRS = [
      ['leftShoulder', 'rightShoulder'],
      ['leftShoulder', 'leftElbow'],
      ['leftElbow', 'leftWrist'],
      ['rightShoulder', 'rightElbow'],
      ['rightElbow', 'rightWrist'],
      ['leftShoulder', 'leftHip'],
      ['rightShoulder', 'rightHip'],
      ['leftHip', 'rightHip'],
      ['leftHip', 'leftKnee'],
      ['leftKnee', 'leftAnkle'],
      ['rightHip', 'rightKnee'],
      ['rightKnee', 'rightAnkle']
    ];

    const W = this.scale.width;
    const H = this.scale.height;

    // Color the skeleton based on the flap state so the player can see
    // when the detector is "armed" for the next stroke.
    const color = this.flap.state === 'up' ? 0xffd24d
                 : this.flap.state === 'down' ? 0x4de0ff
                 : 0x80ff80;

    g.lineStyle(4, color, 0.75);
    g.beginPath();
    for (const [a, b] of PAIRS) {
      const la = pose.getLandmark(a);
      const lb = pose.getLandmark(b);
      if (!la || !lb) continue;
      if (la.visibility != null && la.visibility < VISIBILITY_MIN) continue;
      if (lb.visibility != null && lb.visibility < VISIBILITY_MIN) continue;
      g.moveTo((1 - la.x) * W, la.y * H);
      g.lineTo((1 - lb.x) * W, lb.y * H);
    }
    g.strokePath();

    // Dot each joint.
    g.fillStyle(0xffffff, 0.85);
    const JOINTS = [
      'leftShoulder', 'rightShoulder',
      'leftElbow', 'rightElbow',
      'leftWrist', 'rightWrist',
      'leftHip', 'rightHip',
      'leftKnee', 'rightKnee',
      'leftAnkle', 'rightAnkle'
    ];
    for (const name of JOINTS) {
      const lm = pose.getLandmark(name);
      if (!lm) continue;
      if (lm.visibility != null && lm.visibility < VISIBILITY_MIN) continue;
      g.fillCircle((1 - lm.x) * W, lm.y * H, 5);
    }
  }

  /**
   * Pulsing horizontal guide arc: draws a pair of dashed arrows to
   * visually nudge the player toward the expected arm arc. Fades away
   * once the player has hit 3+ flaps (they know what they're doing).
   */
  _drawFlapGuide() {
    const g = this.guideGraphics;
    g.clear();
    if (this.localFlaps >= 3) return;

    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W * 0.5;
    const cy = H * 0.55;
    const r = Math.min(W, H) * 0.2;

    const pulse = 0.4 + 0.3 * Math.sin(this.elapsed * 3);
    g.lineStyle(3, 0xffffff, pulse);
    // Left arc (from shoulder-level up over the head)
    this._strokeArc(g, cx - r * 1.2, cy, r * 0.8, Math.PI * 0.5, Math.PI * 1.1);
    // Right arc
    this._strokeArc(g, cx + r * 1.2, cy, r * 0.8, Math.PI * -0.1, Math.PI * 0.5);
  }

  /** Stroke a partial circle between start and end radians. */
  _strokeArc(g, cx, cy, r, start, end) {
    const steps = 14;
    g.beginPath();
    for (let i = 0; i <= steps; i++) {
      const t = start + (end - start) * (i / steps);
      const x = cx + Math.cos(t) * r;
      const y = cy + Math.sin(t) * r;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.strokePath();
  }

  // ──────────────────────────────────────────────────────────── HUD

  _buildHud() {
    const W = this.scale.width;
    const H = this.scale.height;

    // Top mini-track HUD: a horizontal strip with one dot per player
    // showing their race progress. Drawn via Graphics every frame so
    // we can also render the finish-line marker on it.
    this.trackHudGraphics = this.add.graphics().setDepth(2000);

    // Personal flap counter (top-left).
    this.localFlapText = this.add.text(18, 18, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#ffd24d',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0, 0).setDepth(2000);

    // Leaderboard text panel (right side so it doesn't collide with the
    // flap counter on narrow portrait screens).
    this.scoreText = this.add.text(W - 18, 18, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'right',
      lineSpacing: 4
    }).setOrigin(1, 0).setDepth(2000);

    // Bottom-center instruction / status line.
    this.statusText = this.add.text(W / 2, H - 24,
      'Swing your arms up and down like wings', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(2000);
  }

  _setStatus(text) {
    if (this.statusText && this.statusText.text !== text) {
      this.statusText.setText(text);
    }
  }

  _renderHud(scores) {
    if (!this.scoreText) return;

    // Right-aligned mini-leaderboard (name + flaps).
    const lines = scores.map((row, i) => {
      const marker = i === 0 ? '★ ' : '  ';
      return marker + row.name + '  ' + row.score + '/' + FLAPS_TO_WIN;
    });
    this.scoreText.setText(lines.join('\n'));

    // Personal counter.
    if (this.localFlapText) {
      const label = (this.sdk.i18n && this.sdk.i18n.t)
        ? this.sdk.i18n.t('bird.flaps', 'Flaps')
        : 'Flaps';
      this.localFlapText.setText(label + ': ' + this.localFlaps + '/' + FLAPS_TO_WIN);
    }

    // Redraw the top race-track HUD.
    this._drawTrackHud(scores);
  }

  /**
   * Mini race track at the top of the screen: a thin horizontal bar with
   * one bird icon per player, positioned proportionally to their flap
   * count. The rightmost end is the finish line, marked with a flag.
   */
  _drawTrackHud(scores) {
    if (!this.trackHudGraphics) return;
    const g = this.trackHudGraphics;
    g.clear();

    const W = this.scale.width;
    const padX = 80;           // leave room for the flap counter + score text
    const y = 58;              // below the flap counter line
    const x0 = padX;
    const x1 = W - padX;
    const len = Math.max(0, x1 - x0);
    if (len < 40) return;

    // Backing rail.
    g.fillStyle(0x000000, 0.35);
    g.fillRoundedRect(x0 - 6, y - 6, len + 12, 12, 6);
    g.lineStyle(2, 0xffffff, 0.6);
    g.strokeRoundedRect(x0 - 6, y - 6, len + 12, 12, 6);

    // Finish flag marker at the right end.
    g.fillStyle(0xffd24d, 0.9);
    g.fillTriangle(x1, y - 14, x1 + 12, y, x1, y + 14);

    // One dot per player, color-tinted from BIRD_TINTS.
    const rows = Array.isArray(scores) ? scores : [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const progress = Math.min(1, (row.score || 0) / FLAPS_TO_WIN);
      const px = x0 + len * progress;
      const tint = BIRD_TINTS[(i + 1) % BIRD_TINTS.length];
      const isLocal = row.id === this.sdk.localPlayerId;
      const r = isLocal ? 9 : 6;
      g.fillStyle(isLocal ? 0xffffff : tint, 1);
      g.fillCircle(px, y, r);
      g.lineStyle(2, 0x000000, 0.7);
      g.strokeCircle(px, y, r);
    }
  }

  /**
   * Reflow HUD when the window resizes (device rotation, split-view).
   * Reposition text, recompute the race track, and snap the bird back
   * onto the new flight lane while keeping its flap-count progress.
   */
  onResize(width, height) {
    if (this.localFlapText) this.localFlapText.setPosition(18, 18);
    if (this.scoreText) this.scoreText.setPosition(width - 18, 18);
    if (this.statusText) this.statusText.setPosition(width / 2, height - 24);

    // Preserve the current progress when the track is recomputed.
    const progress = Math.min(1, this.localFlaps / FLAPS_TO_WIN);
    this._trackStartX = width * 0.1;
    this._trackFinishX = width * 0.9;
    this._flightLaneY = height * 0.58;
    this.localBirdX = this._trackStartX + (this._trackFinishX - this._trackStartX) * progress;
    this.localBirdY = this._flightLaneY;
    this.localBird.up.setPosition(this.localBirdX, this.localBirdY);
    this.localBird.down.setPosition(this.localBirdX, this.localBirdY);
    // Force an immediate HUD redraw with the current scores.
    this._renderHud(this.lb.getAll());
  }

  // ────────────────────────────────────────────────────────── end

  _endMatch() {
    if (this.matchOver) return;
    this.matchOver = true;

    if (this.sdk.audio) {
      this.sdk.audio.stopMusic();
      this.sdk.audio.playSfx('win');
    }

    // Only the host signals the formal end-of-match.
    if (this.sdk.isHost) {
      const all = this.lb.getAll();
      const winner = this.lb.getWinner();
      const scores = {};
      for (const r of all) scores[r.id] = r.score;
      this.sdk.endMatch({
        winnerName: winner ? winner.name : 'tie',
        scores
      });
    }
  }
}
